import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SplitterPanelSize = {
  id: string | number;
  size: number;
  minSize?: number;
};

export function usePersistedSplitterSizes({
  panels,
  setPanels,
}: {
  panels: SplitterPanelSize[];
  setPanels: (panels: SplitterPanelSize[]) => void;
}) {
  const panelsRef = useRef(panels);
  panelsRef.current = panels;

  const persistedSizes = useMemo(
    () => panels.map(({ size }) => size),
    [panels],
  );

  const [controlledSizes, setControlledSizes] =
    useState<number[]>(persistedSizes);

  useEffect(() => {
    setControlledSizes(persistedSizes);
  }, [persistedSizes]);

  const handleResize = useCallback(({ size }: { size: number[] }) => {
    setControlledSizes(size);
  }, []);

  const handleResizeEnd = useCallback(
    ({ size }: { size: number[] }) => {
      setControlledSizes(size);
      setPanels(
        panelsRef.current.map((panel, index) => ({
          ...panel,
          size: size[index] ?? panel.size,
        })),
      );
    },
    [setPanels],
  );

  return {
    panels,
    controlledSizes,
    handleResize,
    handleResizeEnd,
  };
}
